import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import thunk from 'redux-thunk';

import isEqual from 'lodash/isEqual';
import { TypedUseSelectorHook, useSelector } from 'react-redux';
import pageError, { IPageErrorState } from '../../../common/store/common/pageError';
import ranking, { IRankingState } from '../../../common/store/ranking/ranking';
import liveStream, { ILiveStreamState } from '../../common/store/liveStream';
import baseballMlb, { IBaseballMlbState } from '../../common/store/baseballMlb';
import baseballNpb, { IBaseballNpbState } from '../../common/store/baseballNpb';
import globalNavigation, { IGlobalNavigationState } from '../../common/store/globalNavigation';
import horseRacing, { IHorseRacingState } from '../../common/store/horseRacing';
import notice, { INoticeState } from '../../common/store/notice';
import pageData, { IPageDataState } from '../../common/store/pageData';
import masthead, { MastheadState } from '../../common/store/masthead';
import soccerJ1League, { ISoccerJ1LeagueState } from '../../common/store/soccerJ1League';
import soccerJLeagueCup, { ISoccerJLeagueCupState } from '../../common/store/soccerJLeagueCup';
import soccerOverseas, { ISoccerOverseasState } from '../../common/store/soccerOverseas';
import subTopics, { ISubTopicsState } from '../../common/store/subTopics';
import userData, { IUserDataState } from '../../common/store/userData';
import searchConditions, { ISearchConditionsState } from '../../../common/store/search/searchConditions';
import personalizedFeed, { PersonalizedFeedPageCommonState } from '../../common/store/personalizedFeed';
import newsFeed, { INewsFeedState } from './newsFeed';
import newsFeedLocalTab, { INewsFeedLocalTabState } from './newsFeedLocalTab';
import newsFeedTab, { INewsFeedTabState } from './newsFeedTab';
import topics, { ITopicsState } from './topics';
import commentTimelineModule, { CommentTimelineModuleState } from './commentTimelineModule';
import type { FeatureToggleRecord } from '@/shared/FeatureToggle';
import userToken, { type UserTokenState } from '@/public/template/common/userTokenV2/store';

// for Redux DevTools
// 開発環境でのみ読み込む
const composeEnhancers =
  ((process.env.NODE_ENV === 'development-local' || process.env.NODE_ENV === 'development') &&
    typeof window !== 'undefined' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
  compose;

export type IInitialStates = {
  newsFeed?: INewsFeedState;
  topics?: ITopicsState;
  textnewsRanking?: IRankingState;
  videonewsRanking?: IRankingState;
  zasshiRanking?: IRankingState;
  commentRanking?: IRankingState;
  liveStream?: ILiveStreamState;
  userData?: IUserDataState;
  globalNavigation?: IGlobalNavigationState;
  pageData: IPageDataState;
  masthead: MastheadState;
  newsFeedTab?: INewsFeedTabState;
  newsFeedLocalTab?: INewsFeedLocalTabState;
  subTopics?: ISubTopicsState;
  notice?: INoticeState;
  pageError?: IPageErrorState;
  horseRacing?: IHorseRacingState;
  soccerJ1League?: ISoccerJ1LeagueState;
  soccerJLeagueCup?: ISoccerJLeagueCupState;
  soccerOverseas?: ISoccerOverseasState;
  baseballNpb?: IBaseballNpbState;
  baseballMlb?: IBaseballMlbState;
  searchConditions?: ISearchConditionsState;
  userToken: UserTokenState;
  commentTimelineModule?: CommentTimelineModuleState;
  featureToggle?: FeatureToggleRecord | null;
} & Partial<PersonalizedFeedPageCommonState>;

export type ITopStates = {
  newsFeed: INewsFeedState;
  topics: ITopicsState;
  textnewsRanking: IRankingState;
  videonewsRanking: IRankingState;
  zasshiRanking: IRankingState;
  commentRanking: IRankingState;
  liveStream: ILiveStreamState;
  userData: IUserDataState;
  globalNavigation: IGlobalNavigationState;
  pageData: IPageDataState;
  masthead: MastheadState;
  newsFeedTab: INewsFeedTabState;
  newsFeedLocalTab: INewsFeedLocalTabState;
  subTopics: ISubTopicsState;
  notice: INoticeState;
  pageError: IPageErrorState;
  horseRacing: IHorseRacingState;
  soccerJ1League: ISoccerJ1LeagueState;
  soccerJLeagueCup: ISoccerJLeagueCupState;
  soccerOverseas: ISoccerOverseasState;
  baseballNpb: IBaseballNpbState;
  baseballMlb: IBaseballMlbState;
  searchConditions: ISearchConditionsState;
  userToken: UserTokenState;
  commentTimelineModule?: CommentTimelineModuleState;
  featureToggle: FeatureToggleRecord | null;
} & PersonalizedFeedPageCommonState;

export const useTopPageSelector: TypedUseSelectorHook<ITopStates> = selector => useSelector(selector, isEqual);

export default (initialState: IInitialStates) =>
  createStore(
    combineReducers<ITopStates>({
      newsFeed,
      personalizedFeed,
      topics,
      textnewsRanking: ranking,
      videonewsRanking: ranking,
      zasshiRanking: ranking,
      commentRanking: ranking,
      liveStream,
      userData,
      globalNavigation,
      pageData,
      masthead,
      newsFeedTab,
      newsFeedLocalTab,
      subTopics,
      notice,
      pageError,
      horseRacing,
      soccerJ1League,
      soccerJLeagueCup,
      soccerOverseas,
      baseballNpb,
      baseballMlb,
      searchConditions,
      userToken,
      commentTimelineModule,
      featureToggle: (state: FeatureToggleRecord | null = null) => state
    }),
    initialState,
    composeEnhancers(applyMiddleware(thunk))
  );
